// Messaging Context for Provider-Client Direct Messages
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import FirebaseMessagingService from '../services/firebase/FirebaseMessagingService';
import FirebaseActivityService from '../services/firebase/FirebaseActivityService';
import {
  Conversation,
  DirectMessage,
  TypingIndicator,
  SendMessageRequest,
  CreateConversationRequest,
  UnsubscribeFunction
} from '../types/messaging';

interface MessagingContextType {
  // State
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: DirectMessage[];
  typingIndicator: TypingIndicator | null;
  totalUnreadCount: number;
  loading: boolean;
  error: string | null;

  // Actions
  selectConversation: (conversationId: string) => void;
  clearActiveConversation: () => void;
  sendMessage: (content: string) => Promise<void>;
  createConversation: (request: CreateConversationRequest) => Promise<string>;
  markAsRead: () => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  archiveConversation: (conversationId: string) => Promise<void>;
  refreshConversations: () => void;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

interface MessagingProviderProps {
  children: ReactNode;
}

export const MessagingProvider: React.FC<MessagingProviderProps> = ({ children }) => {
  const { user, userProfile, isProvider, isClient } = useAuth();
  const userRole = isProvider ? 'provider' : isClient ? 'client' : null;
  // For phone OTP clients, use linked patientId for conversation queries
  const resolvedUserId = (isClient && userProfile?.patientId) ? userProfile.patientId : user?.uid;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [typingIndicator, setTypingIndicator] = useState<TypingIndicator | null>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const messagingService = FirebaseMessagingService.getInstance();

  // Subscribe to conversations list
  useEffect(() => {
    if (!resolvedUserId || !userRole) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = messagingService.subscribeToConversations(
      resolvedUserId,
      userRole as 'provider' | 'client',
      (convos) => {
        setConversations(convos);
        setLoading(false);
        setError(null);
      }
    );

    return () => unsubscribe();
  }, [resolvedUserId, userRole]);

  // Subscribe to total unread count
  useEffect(() => {
    if (!resolvedUserId || !userRole) {
      setTotalUnreadCount(0);
      return;
    }

    const unsubscribe = messagingService.subscribeToUnreadCount(
      resolvedUserId,
      userRole as 'provider' | 'client',
      setTotalUnreadCount
    );

    return () => unsubscribe();
  }, [resolvedUserId, userRole]);

  // Subscribe to messages when active conversation changes
  // Verify the current user is actually a participant (auth.uid must match conversation data)
  useEffect(() => {
    if (!activeConversation || !user) {
      setMessages([]);
      return;
    }

    // For security rules, auth.uid must match either providerId or clientId
    // For phone OTP users, linkedPatientId() handles the mapping server-side
    const authUid = user.uid;
    const isParticipant =
      authUid === activeConversation.providerId ||
      authUid === activeConversation.clientId;
    const hasLinkedPatient = isClient && userProfile?.patientId &&
      userProfile.patientId === activeConversation.clientId;

    if (!isParticipant && !hasLinkedPatient) {
      console.warn(
        'Messaging: auth.uid does not match conversation participants.',
        { authUid, conversationId: activeConversation.id, clientId: activeConversation.clientId, providerId: activeConversation.providerId, linkedPatientId: userProfile?.patientId }
      );
      setMessages([]);
      return;
    }

    const unsubscribe = messagingService.subscribeToMessages(
      activeConversation.id,
      setMessages
    );

    return () => unsubscribe();
  }, [activeConversation?.id, user?.uid]);

  // Subscribe to typing indicator when active conversation changes
  useEffect(() => {
    if (!activeConversation || !resolvedUserId) {
      setTypingIndicator(null);
      return;
    }

    const unsubscribe = messagingService.subscribeToTypingIndicator(
      activeConversation.id,
      resolvedUserId,
      setTypingIndicator
    );

    return () => unsubscribe();
  }, [activeConversation?.id, resolvedUserId]);

  // Select a conversation
  const selectConversation = useCallback(async (conversationId: string) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    if (conversation) {
      setActiveConversation(conversation);
    } else {
      // Fetch conversation if not in list
      const fetchedConversation = await messagingService.getConversation(conversationId);
      if (fetchedConversation) {
        setActiveConversation(fetchedConversation);
      }
    }
  }, [conversations]);

  // Clear active conversation
  const clearActiveConversation = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
    setTypingIndicator(null);
  }, []);

  // Send a message
  const sendMessage = useCallback(async (content: string) => {
    if (!activeConversation || !resolvedUserId || !userRole) {
      throw new Error('No active conversation or user');
    }

    const isProvider = userRole === 'provider';
    const request: SendMessageRequest = {
      conversationId: activeConversation.id,
      senderId: resolvedUserId,
      senderName: isProvider ? activeConversation.providerName : activeConversation.clientName,
      senderRole: userRole as 'provider' | 'client',
      recipientId: isProvider ? activeConversation.clientId : activeConversation.providerId,
      recipientName: isProvider ? activeConversation.clientName : activeConversation.providerName,
      content
    };

    try {
      await messagingService.sendMessage(request);
      // Clear typing indicator after sending
      setTyping(false);

      // Log activity for client-sent messages (provider sees in Practice Activity)
      if (!isProvider && activeConversation) {
        FirebaseActivityService.getInstance().logActivity({
          providerId: activeConversation.providerId,
          clientId: resolvedUserId,
          clientName: activeConversation.clientName,
          activityType: 'message_sent',
          title: 'New message from client',
          description: `${activeConversation.clientName} sent a message.`,
          metadata: { messagePreview: content.substring(0, 100) }
        }).catch(err => console.warn('[Activity] Message log failed:', err));
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
      throw err;
    }
  }, [activeConversation, resolvedUserId, userRole]);

  // Create a new conversation
  const createConversation = useCallback(async (request: CreateConversationRequest): Promise<string> => {
    try {
      const conversationId = await messagingService.createConversation(request);
      // Select the new conversation
      await selectConversation(conversationId);
      return conversationId;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError('Failed to create conversation');
      throw err;
    }
  }, [selectConversation]);

  // Mark messages as read (only when there are actual messages to mark)
  const markAsRead = useCallback(async () => {
    if (!activeConversation || !resolvedUserId || !userRole || !user) return;

    // Verify auth.uid is a participant before attempting Firestore operations
    const authUid = user.uid;
    const isParticipant =
      authUid === activeConversation.providerId ||
      authUid === activeConversation.clientId;
    const hasLinkedPatient = isClient && userProfile?.patientId &&
      userProfile.patientId === activeConversation.clientId;

    if (!isParticipant && !hasLinkedPatient) return;

    try {
      await messagingService.markMessagesAsRead(
        activeConversation.id,
        resolvedUserId,
        userRole as 'provider' | 'client'
      );
    } catch (err: any) {
      // Silently handle permission errors — logged once, don't spam
      if (err?.code !== 'permission-denied') {
        console.error('Error marking messages as read:', err);
      }
    }
  }, [activeConversation, resolvedUserId, userRole, user?.uid]);

  // Set typing indicator
  const setTyping = useCallback((isTyping: boolean) => {
    if (!activeConversation || !resolvedUserId) return;

    const userName = userRole === 'provider'
      ? activeConversation.providerName
      : activeConversation.clientName;

    messagingService.setTypingIndicator(
      activeConversation.id,
      resolvedUserId,
      userName,
      isTyping
    );
  }, [activeConversation, resolvedUserId, userRole]);

  // Archive a conversation
  const archiveConversation = useCallback(async (conversationId: string) => {
    if (!resolvedUserId) return;

    try {
      await messagingService.archiveConversation(conversationId, resolvedUserId);
      if (activeConversation?.id === conversationId) {
        clearActiveConversation();
      }
    } catch (err) {
      console.error('Error archiving conversation:', err);
      setError('Failed to archive conversation');
      throw err;
    }
  }, [resolvedUserId, activeConversation, clearActiveConversation]);

  // Refresh conversations
  const refreshConversations = useCallback(() => {
    // The subscription will automatically update, but we can trigger a re-render
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  }, []);

  const value: MessagingContextType = {
    conversations,
    activeConversation,
    messages,
    typingIndicator,
    totalUnreadCount,
    loading,
    error,
    selectConversation,
    clearActiveConversation,
    sendMessage,
    createConversation,
    markAsRead,
    setTyping,
    archiveConversation,
    refreshConversations
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessaging = (): MessagingContextType => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export default MessagingContext;
